import React, { useState, useMemo } from 'react';
import { Badge, Button, Card, Empty, Modal, Typography } from 'antd';
import { PlusOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';

const { Text } = Typography;

/* ─── Droppable column body ─────────────────────────────────────────────── */
const DroppableBody = ({ id, isOver, children, style }) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        transition: 'background 0.2s',
        background: isOver ? 'rgba(102,126,234,0.18)' : 'var(--qk-card-bg)',
      }}
    >
      {children}
    </div>
  );
};

/* ─── Draggable card wrapper ─────────────────────────────────────────────── */
const DraggableCard = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: String(id) });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        opacity: isDragging ? 0.35 : 1,
        cursor: 'grab',
        touchAction: 'none',
        outline: 'none',
      }}
    >
      {children}
    </div>
  );
};

/* ─── KanbanBoard ────────────────────────────────────────────────────────── */
/**
 * Props:
 *   columns        : { key, title, color?, icon?, headerBg? }[]
 *   items          : object[]
 *   columnKey      : string          — item dagi ustun maydoni (default: 'status')
 *   rowKey         : string          — noyob kalit maydoni     (default: 'id')
 *   renderCard     : (item, column) => ReactNode
 *   AddComponent   : ReactComponent  — { column, columnKey, onClose, onSuccess }
 *   addModalTitle  : string | (column) => string
 *   onMove         : (item, toColumnKey) => void  — siljitish callback (MAJBURIY)
 *   onRefresh      : () => void
 *   columnMinWidth : number  (default: 280)
 *   emptyText      : string
 */
const KanbanBoard = ({
  columns = [],
  items = [],
  columnKey = 'status',
  rowKey = 'id',
  renderCard,
  AddComponent,
  addModalTitle,
  onMove,
  onRefresh,
  columnMinWidth = 280,
  emptyText = "Ma'lumot yo'q",
}) => {
  const [addOpen, setAddOpen] = useState(false);
  const [activeColumnKey, setActiveColumnKey] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [overColumnKey, setOverColumnKey] = useState(null);

  const isAddValid = typeof AddComponent === 'function';

  /* Sensor: 8px harakat bo'lsa drag boshlanadi — tugma bosganda xato trigger yo'q */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const columnKeys = useMemo(() => columns.map((c) => c.key), [columns]);

  const columnItemsMap = useMemo(() => {
    const map = {};
    columns.forEach((col) => { map[col.key] = []; });
    (Array.isArray(items) ? items : []).forEach((item) => {
      const key = item[columnKey];
      if (map[key]) map[key].push(item);
    });
    return map;
  }, [columns, items, columnKey]);

  const draggingItem = useMemo(
    () => (draggingId != null ? (Array.isArray(items) ? items : []).find((i) => String(i[rowKey]) === String(draggingId)) : null),
    [draggingId, items, rowKey]
  );

  /* ── DnD handlers ── */
  const handleDragStart = ({ active }) => setDraggingId(active.id);

  const handleDragOver = ({ over }) => setOverColumnKey(over ? over.id : null);

  const handleDragEnd = ({ active, over }) => {
    setDraggingId(null);
    setOverColumnKey(null);
    if (!over || !onMove) return;
    const item = (Array.isArray(items) ? items : []).find((i) => String(i[rowKey]) === String(active.id));
    if (item && item[columnKey] !== over.id && columnKeys.includes(over.id)) {
      onMove(item, over.id);
    }
  };

  /* ── Arrow move ── */
  const moveCard = (item, direction) => {
    if (!onMove) return;
    const currentIdx = columnKeys.indexOf(item[columnKey]);
    const nextIdx = currentIdx + direction;
    if (nextIdx < 0 || nextIdx >= columnKeys.length) return;
    onMove(item, columnKeys[nextIdx]);
  };

  /* ── Add modal ── */
  const activeColumn = columns.find((c) => c.key === activeColumnKey) || null;
  const resolvedModalTitle =
    typeof addModalTitle === 'function'
      ? addModalTitle(activeColumn)
      : addModalTitle || "Yangi karta qo'shish";

  const handleClose = () => { setAddOpen(false); setActiveColumnKey(null); };
  const handleSuccess = () => { setAddOpen(false); setActiveColumnKey(null); onRefresh?.(); };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div
          style={{
            display: 'flex',
            gap: 16,
            overflowX: 'auto',
            alignItems: 'flex-start',
            paddingBottom: 12,
          }}
        >
          {columns.map((col, colIdx) => {
            const colItems = columnItemsMap[col.key] || [];
            const accentColor = col.color || '#667eea';
            const isOver = overColumnKey === col.key;

            return (
              <div
                key={col.key}
                style={{
                  minWidth: columnMinWidth,
                  flex: `0 0 ${columnMinWidth}px`,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* ── Header ── */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 14px',
                    background: col.headerBg || 'var(--qk-bg-soft)',
                    borderRadius: '12px 12px 0 0',
                    borderTop: `3px solid ${accentColor}`,
                    borderLeft: '1px solid var(--qk-border)',
                    borderRight: '1px solid var(--qk-border)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {col.icon && <span style={{ color: accentColor, fontSize: 16 }}>{col.icon}</span>}
                    <Text strong style={{ fontSize: 14 }}>{col.title}</Text>
                  </div>
                  <Badge
                    count={colItems.length}
                    showZero
                    style={{ backgroundColor: accentColor, fontSize: 11, minWidth: 20, height: 20, lineHeight: '20px' }}
                  />
                </div>

                {/* ── Cards ── */}
                <DroppableBody
                  id={col.key}
                  isOver={isOver}
                  style={{
                    flex: 1,
                    minHeight: 80,
                    padding: '10px 10px 0',
                    borderLeft: '1px solid var(--qk-border)',
                    borderRight: '1px solid var(--qk-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  {colItems.length === 0 ? (
                    <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '12px 0' }} />
                  ) : (
                    colItems.map((item) => (
                      <DraggableCard key={item[rowKey]} id={item[rowKey]}>
                        <div>
                          {renderCard ? renderCard(item, col) : (
                            <Card size="small" style={{ borderRadius: 8 }}>
                              <Text type="secondary" style={{ fontSize: 12 }}>#{item[rowKey]}</Text>
                            </Card>
                          )}
                          {/* ── Arrow buttons ── */}
                          <div
                            style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end' }}
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            <Button
                              size="small"
                              icon={<LeftOutlined />}
                              disabled={colIdx === 0}
                              onClick={(e) => { e.stopPropagation(); moveCard(item, -1); }}
                              style={{ fontSize: 11, padding: '0 8px', height: 24 }}
                            />
                            <Button
                              size="small"
                              icon={<RightOutlined />}
                              disabled={colIdx === columns.length - 1}
                              onClick={(e) => { e.stopPropagation(); moveCard(item, +1); }}
                              style={{ fontSize: 11, padding: '0 8px', height: 24 }}
                            />
                          </div>
                        </div>
                      </DraggableCard>
                    ))
                  )}
                </DroppableBody>

                {/* ── Add button ── */}
                {isAddValid && (
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => { setActiveColumnKey(col.key); setAddOpen(true); }}
                    style={{
                      borderRadius: '0 0 12px 12px',
                      borderTop: 'none',
                      borderColor: 'var(--qk-border)',
                      color: accentColor,
                      background: 'var(--qk-card-bg)',
                      width: '100%',
                      height: 40,
                    }}
                  >
                    Qo&apos;shish
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Drag overlay (sürüklənən karta ko'rinishi) ── */}
        <DragOverlay>
          {draggingItem ? (
            <div style={{ opacity: 0.9, cursor: 'grabbing', pointerEvents: 'none' }}>
              {renderCard
                ? renderCard(draggingItem, columns.find((c) => c.key === draggingItem[columnKey]))
                : (
                  <Card size="small" style={{ borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
                    <Text>#{draggingItem[rowKey]}</Text>
                  </Card>
                )}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {isAddValid && (
        <Modal
          open={addOpen}
          title={resolvedModalTitle}
          onCancel={handleClose}
          footer={null}
          destroyOnHidden
        >
          <AddComponent
            column={activeColumn}
            columnKey={activeColumnKey}
            onClose={handleClose}
            onSuccess={handleSuccess}
          />
        </Modal>
      )}
    </>
  );
};

export default KanbanBoard;
